/**
 * Trip sharing — the Firestore half.
 *
 * TWO THINGS ARE REQUIRED TO OPEN A TRIP
 * --------------------------------------
 * The link says WHICH invitation. The signed-in account says WHETHER you may
 * have it. Every invitation names one email address, and the rules compare it
 * against the address in the caller's own auth token — so a forwarded link is
 * useless to whoever receives it, and an invitee who has never registered must
 * do so first.
 *
 * That second half is what a link-only model could not do. A read that cannot
 * prove who is asking cannot be withdrawn from one person; it can only be
 * relabelled. Requiring an account turns every read into a read by somebody,
 * and access becomes genuinely revocable.
 *
 * The content still lives ON the invitation rather than in one shared document
 * every guest reads. That was already necessary when the token was the only
 * credential, and it remains the right shape: revoking clears the very
 * document the reader must name.
 *
 *   trip_shares/{shareId}                   OWNER-ONLY bookkeeping. The
 *                                           canonical snapshot to republish
 *                                           from, and the thread id.
 *   trip_invites/{inviteId}                 one per guest. The id is the token
 *                                           in their link; the document carries
 *                                           the snapshot, and is readable only
 *                                           by the address it names.
 *   trip_invite_gates/{inviteId}            public, and deliberately almost
 *                                           empty: a MASKED address, so the
 *                                           sign-in screen can say who to sign
 *                                           in as.
 *   trip_threads/{threadId}                 `memberEmails` — who may read the
 *                                           conversation. Maintained by the
 *                                           owner, because the owner knows the
 *                                           address before the account exists.
 *   trip_threads/{threadId}/comments/{id}   one conversation per trip.
 *
 * The residual this file used to carry — a revoked guest could still read the
 * comment thread — is CLOSED. Reads are gated on the caller's address being in
 * `memberEmails`, and revoking removes it.
 *
 * FAILURE POLICY
 * --------------
 * Reads that feed a decision REJECT rather than resolving empty. This is the
 * opposite of the convention elsewhere in the codebase, and deliberately so:
 * an empty result here is indistinguishable from "no share exists yet", and
 * acting on that mistake mints a second share and orphans every link already
 * sent. Reads that feed only a display still fail soft.
 */

import {
  doc,
  collection,
  arrayUnion,
  arrayRemove,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../config/firebase';
import {
  MAX_COMPANIONS,
  maskEmail,
  newInviteToken,
  newShareId,
  normaliseComment,
  normaliseInviteName,
  requireInviteEmail,
  sortInvites,
} from './tripSharing';

const SHARES = 'trip_shares';
const INVITES = 'trip_invites';
const THREADS = 'trip_threads';

/**
 * A tiny public document per invitation, holding nothing but a MASKED address
 * and whether the invitation is still live.
 *
 * It exists for one reason. The invitation itself can only be read by an
 * account holding the invited address — which is the point — but that means a
 * visitor who is not signed in cannot read anything at all, and the sign-in
 * screen would have to say "sign in" without being able to say who as. People
 * have more than one address; "sign in" alone sends them to guess.
 *
 * So the gate reveals `te****@example.com` and nothing else: enough for the
 * right person to know which account to use, useless to anyone else.
 */
const GATES = 'trip_invite_gates';

/** Comments shown per trip. Beyond this the thread is unreadable anyway. */
const COMMENT_LIMIT = 100;

/* ────────────────────────── owner bookkeeping ─────────────────────── */

/**
 * Creates or refreshes the owner's record of a shared trip.
 *
 * `revoked` and `threadId` are written ONCE, at creation. Writing them on
 * every call is how "stop sharing" silently turned itself back on: the next
 * accommodation pick republished `revoked: false` over the owner's decision.
 */
export async function upsertShare({ ownerUid, snapshot, existingShareId = null, threadId = null }) {
  if (!ownerUid) throw new Error('upsertShare: ownerUid is required');
  if (!snapshot || !snapshot.tripId) throw new Error('upsertShare: snapshot is required');

  const shareId = existingShareId || newShareId();
  const isNew = !existingShareId;
  const thread = threadId || newShareId();

  // The thread document exists so the rules can answer "does this caller own
  // the thread this comment belongs to, and is this reader a member of it?"
  // with one get(). Rules cannot run a query, so both answers have to live
  // under the thread's own id.
  //
  // Written on EVERY call, not only when the share is new. A share created
  // before threads existed carries a threadId pointing at nothing, and a
  // missing thread document poisons everything downstream: revoking fails
  // atomically and rolls back the revocation with it, and inviting throws.
  // Repairing on the way past is cheap; leaving a trip permanently
  // unrevocable is not. `merge` so it never clobbers `memberEmails`.
  await setDoc(
    doc(db, THREADS, thread),
    { threadId: thread, ownerUid, tripId: snapshot.tripId },
    { merge: true }
  );

  await setDoc(
    doc(db, SHARES, shareId),
    {
      shareId,
      ownerUid,
      tripType: snapshot.tripType,
      tripId: snapshot.tripId,
      // Written every time so a share that has none gets one. The rules permit
      // attaching a thread to a share without one, and never re-pointing it.
      threadId: thread,
      snapshot,
      updatedAt: new Date().toISOString(),
      // `revoked` stays create-only: rewriting it is how "stop sharing"
      // silently turned itself back on.
      ...(isNew ? { revoked: false, createdAt: new Date().toISOString() } : {}),
    },
    { merge: true }
  );

  const saved = await getDoc(doc(db, SHARES, shareId));
  return saved.data();
}

/**
 * The share already created for a trip, or null if there is none.
 *
 * THROWS on a failed read rather than returning null. A caller cannot tell a
 * failure from "never shared", and the two lead to opposite actions — reuse
 * the existing share, or mint a new one and strand every link already sent.
 */
export async function findShareForTrip({ ownerUid, tripId }) {
  if (!ownerUid || !tripId) return null;

  const q = query(
    collection(db, SHARES),
    where('ownerUid', '==', ownerUid),
    where('tripId', '==', tripId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  // Deterministic when a duplicate somehow exists: oldest wins, because that
  // is the one the links already sent point at.
  return snap.docs
    .map((d) => d.data())
    .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')))[0];
}

/**
 * Publishes the current snapshot to everyone still on the guest list.
 *
 * The invite is what an invitee reads, so a snapshot that stops at the share
 * document is a snapshot nobody sees. Revoked invites are skipped — refreshing
 * the content of a link that has been withdrawn would put the trip back in
 * front of the person it was withdrawn from.
 */
export async function publishSnapshotToInvites({ shareId, snapshot, invites }) {
  const live = (Array.isArray(invites) ? invites : []).filter((i) => i && !i.revoked);
  if (!live.length) return 0;

  const batch = writeBatch(db);
  for (const invite of live) {
    batch.update(doc(db, INVITES, invite.inviteId), {
      snapshot,
      snapshotUpdatedAt: new Date().toISOString(),
    });
  }
  await batch.commit();
  return live.length;
}

/**
 * Turns sharing off for a whole trip in one action: every live invitation is
 * revoked, and the owner's share is marked so nothing republishes to it.
 */
export async function stopSharing({ shareId, threadId, invites }) {
  const live = (Array.isArray(invites) ? invites : []).filter((i) => i && !i.revoked);

  const batch = writeBatch(db);
  for (const invite of live) {
    batch.update(doc(db, INVITES, invite.inviteId), { revoked: true, snapshot: null });
    batch.delete(doc(db, GATES, invite.inviteId));
  }
  if (threadId) {
    batch.set(doc(db, THREADS, threadId), { memberEmails: [] }, { merge: true });
  }
  if (shareId) {
    batch.update(doc(db, SHARES, shareId), { revoked: true, updatedAt: new Date().toISOString() });
  }
  await batch.commit();
  return live.length;
}

/* ────────────────────────────── invites ───────────────────────────── */

/**
 * Invites one person and returns the stored invite, including its token.
 *
 * The snapshot is written onto the invite at creation, so the link works the
 * instant it is copied rather than after the next publish.
 */
export async function inviteCompanion({
  shareId,
  threadId,
  ownerUid,
  tripType,
  tripId,
  name,
  email = '',
  snapshot,
  currentCount = 0,
  existingEmails = [],
}) {
  const cleanName = normaliseInviteName(name);
  if (!cleanName) throw new Error('Give this person a name so you can tell the list apart.');

  // The address is now the thing the database checks a reader against, so an
  // invitation without one could never be opened by anybody.
  const { email: cleanEmail, error } = requireInviteEmail(email);
  if (error) throw new Error(error);

  if (!shareId || !threadId || !ownerUid || !tripId) {
    throw new Error('inviteCompanion: missing trip details');
  }
  if (currentCount >= MAX_COMPANIONS) {
    throw new Error(`You can invite up to ${MAX_COMPANIONS} people to one trip.`);
  }
  // Membership of the conversation is a set of addresses while invitations are
  // a list of ids, so two invitations to one address means revoking either
  // strips the other's access to the thread. Refusing the duplicate is simpler
  // than reconciling that afterwards.
  if ((existingEmails || []).includes(cleanEmail)) {
    throw new Error('That person is already on this trip.');
  }

  const inviteId = newInviteToken();
  const invite = {
    inviteId,
    shareId,
    threadId,
    ownerUid,
    tripType,
    tripId,
    name: cleanName,
    email: cleanEmail,
    status: 'INVITED',
    revoked: false,
    snapshot: snapshot || null,
    snapshotUpdatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    lastViewedAt: null,
  };

  // Three writes, one meaning. Batched so a guest can never end up half
  // invited: readable but not in the thread, or in the thread with no
  // invitation to read.
  const batch = writeBatch(db);
  batch.set(doc(db, INVITES, inviteId), invite);
  batch.set(doc(db, GATES, inviteId), {
    inviteId,
    emailMasked: maskEmail(cleanEmail),
    tripType,
    revoked: false,
  });
  // Membership of the conversation is kept by ADDRESS, not by account id. The
  // owner knows the address at the moment they invite; they do not know the
  // account id, which may not exist yet — the whole point of requiring
  // registration is that some invitees have not registered.
  batch.update(doc(db, THREADS, threadId), { memberEmails: arrayUnion(cleanEmail) });
  await batch.commit();

  return invite;
}

/**
 * The guest list for one trip. Throws on failure, for the reason
 * findShareForTrip does: an empty list and a failed read lead to opposite
 * actions, and one of them hides live invitations the owner needs to revoke.
 */
export async function listCompanions({ ownerUid, tripId }) {
  if (!ownerUid || !tripId) return [];
  const q = query(
    collection(db, INVITES),
    where('ownerUid', '==', ownerUid),
    where('tripId', '==', tripId)
  );
  const snap = await getDocs(q);
  return sortInvites(snap.docs.map((d) => d.data()));
}

/**
 * Withdraws one person's access — really withdraws it.
 *
 * Clearing `snapshot` is the part that matters. The flag alone would leave the
 * trip sitting in a document the holder can still name, and "removed from the
 * trip" would mean nothing more than a changed label on the owner's screen.
 */
export async function revokeCompanion(invite) {
  const inviteId = typeof invite === 'string' ? invite : invite?.inviteId;
  if (!inviteId) throw new Error('revokeCompanion: an invite is required');

  const batch = writeBatch(db);
  batch.update(doc(db, INVITES, inviteId), { revoked: true, snapshot: null });
  // DELETED, not flagged. A flagged gate still answers "yes, that token was
  // real once" to anyone who asks the REST API directly, and unknown and
  // withdrawn are meant to be indistinguishable.
  batch.delete(doc(db, GATES, inviteId));

  // Removing the address from the thread is what closes the gap a link-only
  // model could not: comment reads are gated on this list, so a revoked guest
  // loses the conversation as well as the trip.
  //
  // `set(merge)` rather than `update`, because a batch is atomic: an `update`
  // against a thread document that does not exist fails, and takes the
  // revocation down with it — leaving the owner told "that did not save" while
  // the guest keeps full access.
  if (typeof invite === 'object' && invite?.threadId && invite?.email) {
    batch.set(
      doc(db, THREADS, invite.threadId),
      { memberEmails: arrayRemove(invite.email) },
      { merge: true }
    );
  }

  await batch.commit();
}

/**
 * Removed deliberately, rather than left as a convenience.
 *
 * It deleted the invitation and nothing else, which left the address in
 * `memberEmails` — so a "deleted" guest kept reading the conversation — and
 * left the gate permanently unmanageable, because the rule that authorises
 * writing it resolves through the invitation that had just been deleted.
 *
 * `revokeCompanion` is the operation that actually withdraws access. A revoked
 * row stays visible in the guest list on purpose: an owner should be able to
 * see that they invited someone and then removed them.
 */

/**
 * Resolves a token from a link into what that person is allowed to see.
 *
 * Returns null for unknown AND for revoked, deliberately: telling a holder
 * which of the two it was tells them whether the token was ever real.
 *
 * Only the fields the shared view needs are returned. `ownerUid` is on the
 * document because the rules need it there and rules cannot project fields —
 * dropping it here does not make it unreachable to someone using the SDK
 * directly, and this is not pretending otherwise. It is an opaque account id
 * that unlocks nothing on its own.
 */
export async function readInvite(token) {
  if (!token) return null;
  try {
    const snap = await getDoc(doc(db, INVITES, token));
    if (!snap.exists()) return null;

    const data = snap.data();
    if (data.revoked || !data.snapshot) return null;

    return {
      inviteId: data.inviteId,
      threadId: data.threadId,
      name: data.name,
      email: data.email,
      tripType: data.tripType,
      snapshot: data.snapshot,
      snapshotUpdatedAt: data.snapshotUpdatedAt || null,
    };
  } catch (error) {
    // A refusal here is the ordinary case, not a fault: it is what the rules
    // do when the caller is signed out, or signed in as somebody else. The
    // gate document tells the page which of those it was.
    console.warn('readInvite: refused or unavailable', error?.code || error);
    return null;
  }
}

/**
 * The public half of an invitation: is it live, and which address is it for.
 *
 * Readable by anyone holding the link, INCLUDING someone not signed in — that
 * is the point. It reveals a masked address and the kind of trip, and nothing
 * else. Returns null when the invitation does not exist or has been withdrawn,
 * and those two are deliberately indistinguishable.
 */
export async function readInviteGate(token) {
  if (!token) return null;
  try {
    const snap = await getDoc(doc(db, GATES, token));
    if (!snap.exists()) return null;
    const data = snap.data();
    return data.revoked ? null : data;
  } catch (error) {
    console.warn('readInviteGate: failed', error?.code || error);
    return null;
  }
}

/**
 * Records that an invitee opened the link.
 *
 * Never throws and never blocks rendering — this is a courtesy, and an invitee
 * whose view could not be recorded should still see the trip.
 */
export async function markInviteViewed(token) {
  if (!token) return;
  try {
    await updateDoc(doc(db, INVITES, token), {
      status: 'VIEWED',
      lastViewedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.warn('markInviteViewed: could not record view', error);
  }
}

/* ────────────────────────────── comments ──────────────────────────── */

/**
 * Posts a comment or a suggestion.
 *
 * `inviteId` names who is speaking and is REQUIRED for a guest — the rules
 * read that invite and refuse the write if it is revoked or belongs to another
 * thread, which is what stops a removed guest posting and what stops anyone
 * claiming to be the organiser. The owner posts with `inviteId: null` and is
 * authorised by being signed in as the trip's owner instead.
 */
export async function postComment({ threadId, inviteId = null, authorName, body, kind = 'COMMENT' }) {
  if (!threadId) throw new Error('postComment: threadId is required');

  const normalised = normaliseComment({ body, authorName, kind });
  if (normalised.error) throw new Error(normalised.error);

  const commentId = newInviteToken();
  await setDoc(doc(db, THREADS, threadId, 'comments', commentId), {
    commentId,
    threadId,
    inviteId,
    authorName: normalised.authorName,
    body: normalised.body,
    kind: normalised.kind,
    hidden: false,
    createdAt: serverTimestamp(),
  });

  return commentId;
}

/**
 * Subscribes to a thread, oldest first so it reads downwards.
 *
 * Returns the unsubscribe — callers must use it as effect cleanup, or every
 * visit stacks another listener (the bug getAllServiceProviders had).
 *
 * Hidden comments are filtered in JS, not in the query, for the reason
 * reviews.js gives: a where('hidden','==',false) silently drops every document
 * written before the field existed.
 */
export function listenComments(threadId, onChange) {
  if (!threadId) return () => {};

  const q = query(
    collection(db, THREADS, threadId, 'comments'),
    orderBy('createdAt', 'asc'),
    limit(COMMENT_LIMIT)
  );

  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => d.data()).filter((c) => !c.hidden)),
    (error) => {
      console.error('listenComments: failed', threadId, error);
      onChange([]);
    }
  );
}

/** The owner can remove something posted on their trip. */
export async function deleteComment(threadId, commentId) {
  if (!threadId || !commentId) return;
  await deleteDoc(doc(db, THREADS, threadId, 'comments', commentId));
}

/* ──────────────────────── the final copy ──────────────────────────── */

/**
 * Queues one email per invitee who gave an address.
 *
 * Returns how many were queued. Never throws: a finalised trip must not be
 * reported as failed because an email did not queue.
 */
export async function sendFinalCopies({ invites, subject, buildHtml }) {
  const recipients = (Array.isArray(invites) ? invites : []).filter(
    (i) => i && !i.revoked && i.email
  );

  let sent = 0;
  for (const invite of recipients) {
    try {
      await setDoc(doc(collection(db, 'mail')), {
        to: invite.email,
        message: { subject, html: buildHtml(invite) },
      });
      sent += 1;
    } catch (error) {
      console.error('sendFinalCopies: could not queue mail for', invite.email, error);
    }
  }

  return sent;
}
